import { useCallback, useEffect, useState } from 'react';
import { api } from '../../../../services/api';
import type {
  CondutorLookup, ElegibilidadeResposta, MunicipioLookup, ParametrosWizard, TarifaResposta,
} from './types';

/**
 * Um veículo que REALMENTE emite: presente nos dois cadastros (CT-e e DER).
 *
 * Antes o wizard cruzava duas listas no cliente, com casamento de placa cru
 * (`d.placa === v.placa`, sem trim nem upper). Não casando, `idVeiculoSgtf`
 * ficava nulo, o veículo seguia selecionável, o CT-e era autorizado na SEFAZ e
 * só então a ATF era recusada — numeração fiscal queimada. A interseção agora é
 * do servidor, e o que não pode emitir simplesmente não vem.
 */
export interface VeiculoApto {
  idVeiculo: number;
  idVeiculoSgtf: string;
  placa: string;
  capacidadePassageiros: number;
  domMarca: string | null;
  domModelo: string | null;
  idMotoristaDono: number;
  nomeMotoristaDono: string | null;
}

interface CondutoresResposta {
  escopo: 'HIERARQUIA' | 'COMPLETO';
  avisoEscopo: string | null;
  data: CondutorLookup[];
}

export function useDadosIniciais() {
  const [condutores, setCondutores] = useState<CondutorLookup[]>([]);
  const [avisoEscopo, setAvisoEscopo] = useState<string | null>(null);
  const [veiculos, setVeiculos] = useState<VeiculoApto[]>([]);
  const [parametros, setParametros] = useState<ParametrosWizard | null>(null);
  const [elegibilidade, setElegibilidade] = useState<ElegibilidadeResposta | null>(null);
  const [tarifa, setTarifa] = useState<TarifaResposta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  /**
   * As cinco leituras são independentes. Com `Promise.all`, uma falha apagava as
   * outras e a tela dizia só "não foi possível carregar": agora ela diz qual, e
   * "Tentar de novo" refaz a carga.
   */
  const carregar = useCallback(() => {
    setCarregando(true);
    setErro(null);
    Promise.allSettled([
      // Recortados pelo grupo financeiro (0035). As rotas de lookup antigas
      // continuam de pé para quem já as consome — inclusive o app.
      api.get<CondutoresResposta>('/cooperado/atf/condutores'),
      api.get<VeiculoApto[]>('/cooperado/atf/veiculos'),
      api.get<ParametrosWizard>('/cooperado/atf/parametros'),
      api.get<ElegibilidadeResposta>('/cooperado/atf/elegibilidade'),
      api.get<TarifaResposta>('/cooperado/atf/tarifa'),
    ]).then(([c, v, p, e, t]) => {
      const falhas: string[] = [];
      if (c.status === 'fulfilled') { setCondutores(c.value.data.data); setAvisoEscopo(c.value.data.avisoEscopo); } else falhas.push('condutores');
      if (v.status === 'fulfilled') setVeiculos(v.value.data); else falhas.push('veículos');
      if (p.status === 'fulfilled') setParametros(p.value.data); else falhas.push('parâmetros da emissão');
      if (e.status === 'fulfilled') setElegibilidade(e.value.data); else falhas.push('situação do cadastro');
      if (t.status === 'fulfilled') setTarifa(t.value.data); else falhas.push('tarifa');
      if (falhas.length) setErro(`Não foi possível carregar: ${falhas.join(', ')}.`);
      setCarregando(false);
    });
  }, []);

  useEffect(carregar, [carregar]);

  /**
   * Reconsulta só a elegibilidade.
   *
   * É o que faz o pagamento por Pix valer: sem isso, o cooperado paga a fatura,
   * volta ao wizard e continua olhando para a mesma tela de bloqueio —
   * concluindo que o pagamento não funcionou. Não refaz a carga inteira, que
   * descartaria o progresso do formulário.
   *
   * Degrada seguro: se a consulta falhar, o veredito anterior é mantido, e quem
   * chamou distingue "consultei e continua devendo" de "não consegui consultar".
   */
  const revalidarElegibilidade = useCallback(async (): Promise<boolean> => {
    try {
      const { data } = await api.get<ElegibilidadeResposta>('/cooperado/atf/elegibilidade');
      setElegibilidade(data);
      return true;
    } catch {
      return false;
    }
  }, []);

  return { condutores, avisoEscopo, veiculos, parametros, elegibilidade, tarifa, carregando, erro, recarregar: carregar, revalidarElegibilidade };
}

export function useMunicipios(query: string) {
  const [resultados, setResultados] = useState<MunicipioLookup[]>([]);
  const [buscando, setBuscando] = useState(false);
  // Falha da busca virava "nenhum resultado", e o cooperado concluía que a cidade
  // não existe.
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) { setResultados([]); setFalhou(false); return; }
    setBuscando(true);
    const timer = setTimeout(() => {
      api.get<MunicipioLookup[]>('/atf/lookup/municipios', { params: { q: query } })
        .then(res => { setResultados(res.data); setFalhou(false); })
        .catch(() => { setResultados([]); setFalhou(true); })
        .finally(() => setBuscando(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  return { resultados, buscando, falhou };
}
